import { useBridgeApi } from '@/api/BridgeApi';
import {
	CATEGORY_LABEL,
	CATEGORY_PILL,
} from '@/spaces/chat/insightCategoryLabels';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import type { CuratedInsightCategory } from '@/types/inbox';
import { cn } from '@/utils/cn';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { trackEvent } from '@/utils/fullstory';

import type {
	CuratedInsightChatPanel,
	CuratedInsightPanelData,
} from '../../../../../bridge/types/state.ts';

const PRIORITY_PILL: Record<string, string> = {
	high: 'bg-rose-50 text-rose-700',
	medium: 'bg-amber-50 text-amber-700',
	low: 'bg-gray-50 text-gray-600',
};

const InsightBody = ({ insight }: { insight: CuratedInsightPanelData }) => {
	const category = insight.category as CuratedInsightCategory;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center gap-2">
				{CATEGORY_LABEL[category] && (
					<span
						className={cn(
							'rounded-full px-2.5 py-0.5 text-xs font-medium',
							CATEGORY_PILL[category] ?? 'bg-gray-100 text-gray-700',
						)}
					>
						{CATEGORY_LABEL[category]}
					</span>
				)}
				<span
					className={cn(
						'rounded-full px-2.5 py-0.5 text-xs font-medium',
						PRIORITY_PILL[insight.priority] ?? PRIORITY_PILL.medium,
					)}
				>
					{insight.priority} priority
				</span>
			</div>

			{insight.summary && (
				<p className="text-sm leading-relaxed text-(--text-secondary)">
					{insight.summary}
				</p>
			)}

			{insight.body_text && (
				<div className="rounded-lg border border-(--glass-border) p-4">
					<p
						className="text-sm leading-relaxed whitespace-pre-wrap
							text-(--text-primary)"
					>
						{insight.body_text}
					</p>
				</div>
			)}

			<span className="text-xs text-(--text-disabled)">
				{formatRelativeTime(insight.created_at)}
			</span>
		</div>
	);
};

interface InsightPanelProps {
	panel: CuratedInsightChatPanel;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const InsightPanel = ({
	panel,
	onBack,
	onClose,
	style,
}: InsightPanelProps) => {
	const bridgeApi = useBridgeApi();

	const handleDismiss = () => {
		trackEvent('Insight Dismissed', {
			curated_insight_id: panel.item_id,
			category: panel.insight?.category,
		});
		bridgeApi.sendAction({
			type: 'do_dismiss_curated_insight',
			curated_insight_id: panel.item_id,
		});
		onClose();
	};

	return (
		<ChatPanelLayout
			title={panel.insight?.title ?? 'Insight'}
			description={panel.insight?.summary}
			isLoading={panel.status === 'loading'}
			onBack={onBack}
			onClose={onClose}
			style={style}
			actionRenderer={() => (
				<button
					type="button"
					onClick={handleDismiss}
					className="rounded-lg border border-(--glass-border) px-3 py-1.5
						text-xs font-medium text-(--text-secondary) transition-colors
						hover:bg-black/[0.02]"
				>
					Dismiss
				</button>
			)}
		>
			{panel.status === 'error' && (
				<div className="flex flex-1 items-center justify-center p-4">
					<p className="text-sm text-rose-600">
						{panel.error ?? 'Failed to load insight'}
					</p>
				</div>
			)}

			{panel.status === 'ready' && panel.insight && (
				<InsightBody insight={panel.insight} />
			)}
		</ChatPanelLayout>
	);
};
